import { useEffect, useRef, useState, useCallback } from 'react';

export const useGameSocket = (url: string, onMessage?: (data: any) => void) => {
    const [latestMessage, setLatestMessage] = useState<any>(null);
    const [isConnected, setIsConnected] = useState(false);
    const reconnectTimerRef = useRef<NodeJS.Timeout | null>(null);
    const socketRef = useRef<WebSocket | null>(null);

    const connect = useCallback(() => {
        if (!url) return;

        if (socketRef.current && (socketRef.current.readyState === WebSocket.OPEN || socketRef.current.readyState === WebSocket.CONNECTING)) {
            return;
        }

        console.log("Connecting to:", url);
        const ws = new WebSocket(url);
        socketRef.current = ws;

        ws.onopen = () => {
            console.log("WebSocket connected!");
            setIsConnected(true);
            if (reconnectTimerRef.current) {
                clearTimeout(reconnectTimerRef.current);
                reconnectTimerRef.current = null;
            }
        };

        ws.onmessage = (event) => {
            let messagePromise;
            if (typeof event.data === 'string') {
                messagePromise = Promise.resolve(event.data);
            } else if (event.data instanceof Blob) {
                messagePromise = event.data.text();
            } else {
                console.warn("Unsupported message data type:", typeof event.data);
                return;
            }

            messagePromise.then((text) => {
                try {
                    const data = JSON.parse(text);
                    console.log("Socket received:", data.type || 'UNKNOWN', data);

                    // Update state for compatibility with existing code
                    setLatestMessage(data);

                    // Call callback immediately for reliable event-driven handling
                    if (onMessage) {
                        onMessage(data);
                    }
                } catch (e) {
                    console.error("Failed to parse message:", e, text);
                }
            }).catch((err) => {
                console.error("Failed to read message data:", err);
            });
        };

        ws.onclose = (event) => {
            console.log("WebSocket closed:", event.code, event.reason);
            setIsConnected(false);
            socketRef.current = null;

            // Auto-reconnect after 3 seconds if not intentionally closed
            if (event.code !== 1000 && url) {
                console.log("Attempting to reconnect in 3s...");
                if (!reconnectTimerRef.current) {
                    reconnectTimerRef.current = setTimeout(connect, 3000);
                }
            }
        };

        ws.onerror = (error) => {
            console.error("WebSocket error:", error);
            // On error, let onclose handle the reconnect logic
        };
    }, [url, onMessage]);

    const sendMessage = useCallback((msg: any) => {
        if (socketRef.current && socketRef.current.readyState === WebSocket.OPEN) {
            try {
                socketRef.current.send(JSON.stringify(msg));
                return true;
            } catch (err) {
                console.error("Failed to send message:", err);
                return false;
            }
        } else {
            console.warn("Cannot send message: WebSocket not open", {
                readyState: socketRef.current?.readyState,
                url
            });
            return false;
        }
    }, [url]);

    useEffect(() => {
        connect();

        return () => {
            if (reconnectTimerRef.current) clearTimeout(reconnectTimerRef.current);
            if (socketRef.current) {
                socketRef.current.onclose = null; // Prevent reconnect on intentional close
                socketRef.current.close(1000, "Component unmounting");
            }
        };
    }, [connect]);

    return {
        socket: socketRef.current, // Keep for backward compat if needed, but use sendMessage and isConnected
        isConnected,
        latestMessage,
        sendMessage
    };
};
